import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useRef,
  type ReactNode,
} from "react";

export type WindowState = "open" | "minimized" | "closed";

export interface WindowOptions {
  width?: number;
  height?: number;           // initial inner height; resize handle persists changes
  minWidth?: number;
  minHeight?: number;
  centered?: boolean;        // center on screen at open time
  offsetXPercent?: number;   // shift X position by this % of viewport (e.g. 0.15 = 15% right)
  offsetYPercent?: number;   // shift Y position by this % of viewport (e.g. 0.05 = 5% down)
  contentOverflowY?: "auto" | "hidden" | "scroll";
  startMaximized?: boolean;  // open in maximized state
  startClosed?: boolean;     // register but don't auto-open; user clicks desktop icon to open
}

export const DEFAULT_MIN_WIDTH = 320;
export const DEFAULT_MIN_HEIGHT = 240;

export interface WindowRecord {
  path: string;
  title: string;
  state: WindowState;
  x: number;
  y: number;
  width: number;             // mutated by resizeWindow; initialized from options.width
  height: number;            // mutated by resizeWindow; initialized from options.height
  zIndex: number;
  maximized: boolean;
  options: WindowOptions;  // always present; defaults to {} on registration
}

export interface WindowContextValue {
  windows: WindowRecord[];
  // Content cache: path -> latest rendered children
  contentCache: React.MutableRefObject<Map<string, ReactNode>>;
  getWindow: (path: string) => WindowRecord | undefined;
  registerWindow: (path: string, title: string, content: ReactNode, options?: WindowOptions) => void;
  setContent: (path: string, content: ReactNode) => void;
  openWindow: (path: string) => void;
  minimizeWindow: (path: string) => void;
  closeWindow: (path: string) => void;
  focusWindow: (path: string) => void;
  moveWindow: (path: string, x: number, y: number) => void;
  resizeWindow: (path: string, width: number, height: number) => void;
  toggleMaximize: (path: string) => void;
}

const WindowContext = createContext<WindowContextValue>({
  windows: [],
  contentCache: { current: new Map() },
  getWindow: () => undefined,
  registerWindow: (_path, _title, _content, _options) => {},
  setContent: () => {},
  openWindow: () => {},
  minimizeWindow: () => {},
  closeWindow: () => {},
  focusWindow: () => {},
  moveWindow: () => {},
  resizeWindow: () => {},
  toggleMaximize: () => {},
});

let zCounter = 100;
const CASCADE = 30;

export function WindowProvider({ children }: { children: ReactNode }) {
  const [windows, setWindows] = useState<WindowRecord[]>([]);
  const countRef = useRef(0);
  const contentCache = useRef<Map<string, ReactNode>>(new Map());

  const update = useCallback((path: string, patch: Partial<WindowRecord>) => {
    setWindows((ws) => ws.map((w) => (w.path === path ? { ...w, ...patch } : w)));
  }, []);

  const bringToFront = useCallback((path: string) => {
    zCounter += 1;
    update(path, { zIndex: zCounter });
  }, [update]);

  const registerWindow = useCallback((path: string, title: string, content: ReactNode, options: WindowOptions = {}) => {
    // Write content to cache atomically before the state update so WindowLayer
    // never renders a registered window with missing content.
    contentCache.current.set(path, content);

    // Compute position outside the setWindows updater -- updaters must be pure.
    const TASKBAR_H = 58;
    const TASKBAR_GAP = 8; // 0.5rem spacing above taskbar
    const winW = options.width ?? 760;
    const winH = options.height ?? 520;
    const vw = typeof window !== "undefined" ? window.innerWidth : 1280;
    const vh = typeof window !== "undefined" ? window.innerHeight : 800;
    const usableH = vh - TASKBAR_H - TASKBAR_GAP;
    const centeredX = Math.max(0, Math.round((vw - winW) / 2));
    // Place ~8% from top of usable area (above taskbar)
    const centeredY = Math.max(0, Math.round(usableH * 0.08));

    setWindows((ws) => {
      if (ws.find((w) => w.path === path)) {
        return ws;
      }
      const n = countRef.current++;
      zCounter += 1;
      const offsetXPx = Math.round((options.offsetXPercent ?? 0) * vw);
      const offsetYPx = Math.round((options.offsetYPercent ?? 0) * vh);
      const rawX = (options.centered ? centeredX : 80 + (n % 6) * CASCADE) + offsetXPx;
      const rawY = (options.centered ? centeredY : 40 + (n % 6) * CASCADE) + offsetYPx;
      // Clamp: keep window within viewport and above taskbar
      const x = Math.max(0, Math.min(vw - 100, rawX));
      const y = Math.max(0, Math.min(usableH - 100, rawY));
      return [
        ...ws,
        {
          path,
          title,
          state: options.startClosed ? "closed" : "open",
          x,
          y,
          width: winW,
          height: winH,
          zIndex: zCounter,
          maximized: options.startMaximized ?? false,
          options,
        },
      ];
    });
  }, []);

  const setContent = useCallback((path: string, content: ReactNode) => {
    contentCache.current.set(path, content);
  }, []);

  const openWindow = useCallback((path: string) => {
    setWindows((ws) => {
      const existing = ws.find((w) => w.path === path);
      if (!existing) return ws;
      zCounter += 1;
      return ws.map((w) =>
        w.path === path ? { ...w, state: "open", zIndex: zCounter } : w
      );
    });
  }, []);

  const minimizeWindow = useCallback((path: string) => update(path, { state: "minimized" }), [update]);
  const closeWindow = useCallback((path: string) => update(path, { state: "closed" }), [update]);
  const focusWindow = useCallback((path: string) => bringToFront(path), [bringToFront]);
  const moveWindow = useCallback((path: string, x: number, y: number) => update(path, { x, y }), [update]);
  const resizeWindow = useCallback((path: string, width: number, height: number) => {
    setWindows((ws) =>
      ws.map((w) => {
        if (w.path !== path) return w;
        const minW = w.options?.minWidth ?? DEFAULT_MIN_WIDTH;
        const minH = w.options?.minHeight ?? DEFAULT_MIN_HEIGHT;
        return {
          ...w,
          width: Math.max(minW, Math.round(width)),
          height: Math.max(minH, Math.round(height)),
        };
      }),
    );
  }, []);

  const toggleMaximize = useCallback((path: string) => {
    setWindows((ws) => ws.map((w) => w.path === path ? { ...w, maximized: !w.maximized } : w));
    bringToFront(path);
  }, [bringToFront]);

  const windowsRef = useRef(windows);
  windowsRef.current = windows;
  const getWindow = useCallback((path: string) => windowsRef.current.find((w) => w.path === path), []);

  const value = useMemo(() => ({
    windows,
    contentCache,
    getWindow,
    registerWindow,
    setContent,
    openWindow,
    minimizeWindow,
    closeWindow,
    focusWindow,
    moveWindow,
    resizeWindow,
    toggleMaximize,
  }), [windows, contentCache, getWindow, registerWindow, setContent, openWindow, minimizeWindow, closeWindow, focusWindow, moveWindow, resizeWindow, toggleMaximize]);

  return (
    <WindowContext.Provider value={value}>
      {children}
    </WindowContext.Provider>
  );
}

export function useWindowContext() {
  return useContext(WindowContext);
}
